import React, { useState } from 'react';
import { ChevronDown, Info, Plus, X } from 'lucide-react';
import { Button } from '../SubscriptionAppButton';
import { ProductSourceCard } from '../pack-sizes/ProductSourceCard';
import { ProductSourceModal } from '../ProductSourceModal';
import { COLLECTION_PRODUCT_COUNTS, TIER_COLLECTIONS } from '../../utils/tierBenefits';
import type { TierBenefitsPatch, TierDiscount, TierDiscountScope, TierFreeShipping, TierFreeShippingMode } from '../../types/loyaltyTiers';
interface TierBenefitsEditorProps {
  discount: TierDiscount;
  freeShipping: TierFreeShipping;
  onChange: (patch: TierBenefitsPatch) => void;
}
const SCOPE_OPTIONS: {
  value: TierDiscountScope;
  label: string;
}[] = [{
  value: 'all',
  label: 'All products'
}, {
  value: 'collections',
  label: 'Collections'
}, {
  value: 'products',
  label: 'Specific products'
}];
const SHIPPING_OPTIONS: {
  value: TierFreeShippingMode;
  label: string;
}[] = [{
  value: 'off',
  label: 'Off'
}, {
  value: 'all',
  label: 'On all orders'
}, {
  value: 'over',
  label: 'On orders over'
}];
const inputClass = 'w-full h-9 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] bg-white focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none disabled:bg-[#F1F5F9] disabled:text-[#94A3B8]';
function CollectionSelect({
  options,
  value,
  label,
  onChange
}: {
  options: string[];
  value: string;
  label: string;
  onChange: (collection: string) => void;
}) {
  return <div className="relative" data-id="element-3371" data-mp="2n6xik6dnlhwc">
      <select value={value} onChange={e => onChange(e.target.value)} aria-label={label} className={`${inputClass} pl-3 pr-8 appearance-none`} data-id="element-3372" data-mp="2n64g3hlq9ek0">
        <option value="" disabled data-id="element-3373" data-mp="2r6enmyp64d0z">
          Choose a collection
        </option>
        {options.map(collection => <option key={collection} value={collection} data-id="element-3374" data-mp="2w12gf9r7ftld">
            {collection}
          </option>)}
      </select>
      <ChevronDown className="w-4 h-4 text-[#94A3B8] absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-3375" data-mp="23ix2vn4bmf7i" />
    </div>;
}
export function TierBenefitsEditor({
  discount,
  freeShipping,
  onChange
}: TierBenefitsEditorProps) {
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  // Collection currently being swapped via its card's "Change" link
  const [changingCollection, setChangingCollection] = useState<string | null>(null);
  // Shows the "add another collection" chooser; open by default when none are chosen
  const [addingCollection, setAddingCollection] = useState(false);
  const update = (patch: Partial<TierDiscount>) => onChange({
    discount: {
      ...discount,
      ...patch
    }
  });
  const unusedCollections = TIER_COLLECTIONS.filter(collection => !discount.collections.includes(collection));
  const showAddChooser = addingCollection || discount.collections.length === 0;
  return <>
      {/* Discount */}
      <div className="mb-4" data-id="element-3376" data-mp="c3md47ue92ph">
        <div className="flex items-start justify-between gap-4 mb-3" data-id="element-3377" data-mp="83u7o40arcoc">
          <div data-id="element-3378" data-mp="85wygq99x9u4">
            <label className="block text-xs font-medium text-[#475569] mb-1" data-id="element-3379" data-mp="866t4r59k565">
              Discount
            </label>
            <p className="text-xs text-[#64748B]" data-id="element-3380" data-mp="kmzcv97cjwhv">
              Applied automatically at checkout for signed-in members in this
              tier.
            </p>
          </div>
          <button type="button" role="switch" aria-checked={discount.enabled} aria-label="Tier discount" onClick={() => update({
          enabled: !discount.enabled
        })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${discount.enabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-3381" data-mp="aoa54995zsfc">
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${discount.enabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-3382" data-mp="tmutvunl2lpt" />
          </button>
        </div>

        {discount.enabled ? <div className="bg-white border border-[#E2E8F0] rounded-md p-3" data-id="element-3383" data-mp="1m3ypqtyy4fcz">
            <div className="flex items-center gap-2" data-id="element-3384" data-mp="3uki0sllfarg7">
              <div className="relative w-24 flex-shrink-0" data-id="element-3385" data-mp="3um2vqcbngcel">
                <input type="number" min={0} max={100} value={discount.percent || ''} onChange={e => update({
              percent: Math.min(100, Math.max(0, Number(e.target.value) || 0))
            })} aria-label="Discount percentage" className={`${inputClass} pl-3 pr-7`} data-id="element-3386" data-mp="3pkvl85i4gz48" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#94A3B8]" data-id="element-3387" data-mp="h5lntap6gi2s">
                  %
                </span>
              </div>
              <span className="text-xs text-[#64748B] flex-shrink-0" data-id="element-3388" data-mp="c70mt923puda">off</span>
              <div className="relative flex-1 min-w-0" data-id="element-3389" data-mp="7eqrtem4q445">
                <select value={discount.appliesTo} onChange={e => update({
              appliesTo: e.target.value as TierDiscountScope
            })} aria-label="Applies to" className={`${inputClass} pl-3 pr-8 appearance-none`} data-id="element-3390" data-mp="2gp5mktepoog">
                  {SCOPE_OPTIONS.map(option => <option key={option.value} value={option.value} data-id="element-3391" data-mp="v3tj7e6kfbbu">
                      {option.label}
                    </option>)}
                </select>
                <ChevronDown className="w-4 h-4 text-[#94A3B8] absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-3392" data-mp="17f2lifwq52nz" />
              </div>
            </div>

            {discount.appliesTo === 'collections' && <div className="mt-3 space-y-2" data-id="element-3393" data-mp="1he1uy0pbd2c1">
                {discount.collections.map(collection => <div key={collection} className="space-y-2" data-id="element-3394" data-mp="1mdejcpvu2hgq">
                    <div className="flex items-center gap-2" data-id="element-3395" data-mp="1mas8ag9qhjtr">
                      <div className="flex-1 min-w-0" data-id="element-3396" data-mp="1qd5fl944ppsn">
                        <ProductSourceCard collectionName={collection} productCount={COLLECTION_PRODUCT_COUNTS[collection] ?? 0} onChange={() => setChangingCollection(changingCollection === collection ? null : collection)} data-id="element-3397" data-mp="1v5gfibyvdbl7" />
                      </div>
                      <button type="button" onClick={() => update({
                collections: discount.collections.filter(c => c !== collection)
              })} aria-label={`Remove ${collection}`} className="p-1.5 rounded hover:bg-[#FEE2E2] text-[#94A3B8] hover:text-[#EF4444] transition-colors flex-shrink-0" data-id="element-3398" data-mp="254xj8nuk9fej">
                        <X className="w-4 h-4" data-id="element-3399" data-mp="1fd4aytesxgn3" />
                      </button>
                    </div>
                    {changingCollection === collection && <CollectionSelect options={[collection, ...unusedCollections]} value={collection} label={`Replace ${collection}`} onChange={next => {
              update({
                collections: discount.collections.map(c => c === collection ? next : c)
              });
              setChangingCollection(null);
            }} data-id="element-3400" data-mp="1pcv7t1qu47qc" />}
                  </div>)}

                {showAddChooser && unusedCollections.length > 0 && <CollectionSelect options={unusedCollections} value="" label="Add collection" onChange={next => {
            update({
              collections: [...discount.collections, next]
            });
            setAddingCollection(false);
          }} data-id="element-3401" data-mp="2857e1zklzt5r" />}

                {!showAddChooser && unusedCollections.length > 0 && <Button variant="ghost" size="sm" iconLeft={<Plus className="w-4 h-4" data-id="element-3403" data-mp="2uckyqbpu913k" />} onClick={() => setAddingCollection(true)} data-id="element-3402" data-mp="2pfbav0i3kxhu">
                    Add collection
                  </Button>}
              </div>}

            {discount.appliesTo === 'products' && <div className="mt-3" data-id="element-3404" data-mp="2klglphjq3qbd">
                <ProductSourceCard collectionName={discount.productIds.length > 0 ? 'Selected products' : 'No products selected'} productCount={discount.productIds.length} onChange={() => setProductPickerOpen(true)} data-id="element-3405" data-mp="2kkf8p7j5ctr0" />
              </div>}
          </div> : <p className="text-sm text-[#94A3B8]" data-id="element-3406" data-mp="1wp2r547wy515">No discount</p>}

        <div className="flex items-start gap-2 mt-3 text-xs text-[#64748B]" data-id="element-3407" data-mp="1wqnkiqihrhxf">
          <Info className="w-3.5 h-3.5 text-[#94A3B8] flex-shrink-0 mt-0.5" data-id="element-3408" data-mp="21o5vsnjze98l" />
          <span data-id="element-3409" data-mp="3r3lwdejuammv">
            Tier discounts don't stack with the club discount. The higher of
            the two applies.
          </span>
        </div>
      </div>

      {/* Free shipping */}
      <div className="mb-4" data-id="element-3410" data-mp="2itr4uix3al2e">
        <label className="block text-xs font-medium text-[#475569] mb-1" data-id="element-3411" data-mp="2iu0w09tvey9o">
          Free shipping
        </label>
        <p className="text-xs text-[#64748B] mb-2" data-id="element-3412" data-mp="2evbkynwb1st7">
          Applies to online orders only.
        </p>
        <div role="radiogroup" aria-label="Free shipping" className="space-y-2" data-id="element-3413" data-mp="2a05q2zkxd3sz">
          {SHIPPING_OPTIONS.map(option => {
          const selected = freeShipping.mode === option.value;
          return <div key={option.value} className="flex items-center gap-3 min-h-[36px]" data-id="element-3414" data-mp="201ybuoi0szuo">
                <button type="button" role="radio" aria-checked={selected} onClick={() => onChange({
              freeShipping: {
                ...freeShipping,
                mode: option.value
              }
            })} className="flex items-center gap-2.5 text-sm text-[#0F172A]" data-id="element-3415" data-mp="1v7cazf9kepou">
                  <span className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${selected ? 'border-[#0EA5E9]' : 'border-[#CBD5E1] bg-white'}`} data-id="element-3416" data-mp="3lgw0yw0amjsx">
                    {selected && <span className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-3417" data-mp="3o1k8kldwfqrf" />}
                  </span>
                  {option.label}
                </button>
                {option.value === 'over' && selected && <div className="relative w-32" data-id="element-3418" data-mp="1n0uyqwr0fm0">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#94A3B8]" data-id="element-3419" data-mp="6lc5l3uvau5a">
                      $
                    </span>
                    <input type="number" min={0} value={freeShipping.minimum || ''} onChange={e => onChange({
                freeShipping: {
                  ...freeShipping,
                  minimum: Math.max(0, Number(e.target.value) || 0)
                }
              })} placeholder="249" aria-label="Order minimum for free shipping" className={`${inputClass} pl-7 pr-3`} data-id="element-3420" data-mp="35529tk0muqlo" />
                  </div>}
              </div>;
        })}
        </div>
      </div>

      <ProductSourceModal isOpen={productPickerOpen} onClose={() => setProductPickerOpen(false)} mode="custom" initialSelectedIds={discount.productIds} onConfirm={selectedIds => {
      update({
        productIds: selectedIds
      });
      setProductPickerOpen(false);
    }} data-id="element-3421" data-mp="yc06cesmpxif" />
    </>;
}